// The operator's privacy, terms and support links (`/api/config`
// `operator_links`), as the footer shows them. changelist ships no such
// pages; a link appears only when the operator configured it.

/** `operator_links` from `/api/config`: each URL, or null when not set. */
export interface OperatorLinkUrls {
  privacy: string | null;
  terms: string | null;
  support: string | null;
}

export interface OperatorLink {
  label: string;
  href: string;
  /** Opens in a new tab: a web page. A `mailto:` link hands off to the mail client instead. */
  newTab: boolean;
}

/** The order links are shown in, with their labels (as on the server's pages). */
const LABELS: [keyof OperatorLinkUrls, string][] = [
  ['privacy', 'Privacy'],
  ['terms', 'Terms'],
  ['support', 'Support'],
];

/** `href`'s scheme when it is http(s) or mailto:, the only kinds the server accepts. */
function scheme(href: string): string | null {
  try {
    const p = new URL(href).protocol;
    return ['http:', 'https:', 'mailto:'].includes(p) ? p : null;
  } catch {
    return null;
  }
}

/** The link for `key`, when its URL is set and usable. */
function linkFor(urls: OperatorLinkUrls | null | undefined, key: keyof OperatorLinkUrls, label: string): OperatorLink | null {
  const href = urls?.[key];
  const kind = href ? scheme(href) : null;
  return href && kind ? { label, href, newTab: kind !== 'mailto:' } : null;
}

/** The links that are set, in display order. */
export function operatorLinks(urls: OperatorLinkUrls | null | undefined): OperatorLink[] {
  return LABELS.flatMap(([key, label]) => linkFor(urls, key, label) ?? []);
}

/** The support link, when the operator set one. */
export function supportLink(urls: OperatorLinkUrls | null | undefined): OperatorLink | null {
  return linkFor(urls, 'support', 'Support');
}
